import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  console.warn(
    'Missing Supabase environment variables. Please set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY.'
  );
}

/**
 * Derive the localStorage key the supabase-js client uses to persist the
 * session. This must match the format produced by gotrue-js so that we can
 * inspect/clean it before the client is instantiated.
 */
function getAuthStorageKey(url: string): string | null {
  try {
    // gotrue-js builds the key as `sb-<project-ref>-auth-token`, where the
    // project ref is the first label of the supabase host
    // (e.g. https://abcdef.supabase.co -> "abcdef").
    const host = new URL(url).hostname;
    const projectRef = host.split('.')[0];
    if (!projectRef) return null;
    return `sb-${projectRef}-auth-token`;
  } catch {
    return null;
  }
}

const AUTH_STORAGE_KEY = getAuthStorageKey(supabaseUrl || '');

/**
 * Remove a stored Supabase session from localStorage. We use this when we
 * detect that the persisted refresh token is no longer valid so the SDK does
 * not enter an infinite "Invalid Refresh Token" loop and so a fresh signUp /
 * signIn request is sent without a stale Authorization header.
 */
export function clearStoredSupabaseSession(): void {
  if (typeof window === 'undefined') return;
  try {
    if (AUTH_STORAGE_KEY) {
      window.localStorage.removeItem(AUTH_STORAGE_KEY);
    }
    // Belt-and-braces: remove any other sb-*-auth-token keys that may linger
    // from older client versions or different project refs.
    for (let i = window.localStorage.length - 1; i >= 0; i--) {
      const key = window.localStorage.key(i);
      if (key && /^sb-.*-auth-token$/.test(key)) {
        window.localStorage.removeItem(key);
      }
    }
  } catch {
    // localStorage may be unavailable (private mode, SSR) – ignore.
  }
}

/**
 * Best-effort sanity check on the persisted session before the supabase-js
 * client boots. If the stored payload is malformed, missing a refresh token,
 * or has an obviously expired refresh token, drop it so the client starts
 * cleanly and we never hit the "Refresh Token Not Found" 400 from the auth
 * server during page load.
 */
function purgeInvalidStoredSession(): void {
  if (typeof window === 'undefined' || !AUTH_STORAGE_KEY) return;
  try {
    const raw = window.localStorage.getItem(AUTH_STORAGE_KEY);
    if (!raw) return;

    const parsed = JSON.parse(raw);
    const session = parsed?.currentSession ?? parsed;

    // Missing refresh token → nothing to refresh, the SDK will just error.
    if (!session?.refresh_token || !session?.access_token) {
      clearStoredSupabaseSession();
      return;
    }

    // expires_at is in seconds since epoch. If it expired more than 30 days
    // ago the refresh token has almost certainly been rotated/revoked
    // server-side, so don't even attempt to refresh.
    const expiresAt = Number(session.expires_at) || 0;
    const thirtyDaysAgo = Math.floor(Date.now() / 1000) - 30 * 24 * 60 * 60;
    if (expiresAt && expiresAt < thirtyDaysAgo) {
      clearStoredSupabaseSession();
    }
  } catch {
    // Malformed JSON – drop it.
    clearStoredSupabaseSession();
  }
}

purgeInvalidStoredSession();

// Globally swallow the noisy "Invalid Refresh Token: Refresh Token Not Found"
// rejection that supabase-js can emit from `_recoverAndRefresh` on startup.
// The SDK already removes the stale session internally; we just don't want it
// to surface as an uncaught error in the console (and in Sentry).
if (typeof window !== 'undefined') {
  window.addEventListener('unhandledrejection', (event) => {
    const message =
      (event.reason && (event.reason.message || String(event.reason))) || '';
    if (
      typeof message === 'string' &&
      /Refresh Token (Not Found|Already Used|Expired)|Invalid Refresh Token/i.test(
        message
      )
    ) {
      clearStoredSupabaseSession();
      event.preventDefault();
    }
  });
}

export const supabase = createClient(
  supabaseUrl || 'https://placeholder.supabase.co',
  supabaseAnonKey || 'placeholder-key',
  {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
      flowType: 'pkce',
    },
    realtime: {
      params: {
        eventsPerSecond: 10,
      },
    },
  }
);

export const getCurrentUser = async () => {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  return user;
};

export const signOut = async () => {
  const { error } = await supabase.auth.signOut();
  return { error };
};
